import { inject, Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Bundle, ServiceRequest } from 'fhir/r4';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { backendEndPointToken } from '../app.config';
import { HttpClient, HttpContext } from '@angular/common/http';
import { LoadingUIEnabled } from '../loading.interceptor';
import { map, catchError } from 'rxjs/operators';
import { ServiceRequestsService } from './service-requests.service';
import { LabBillingPlaceholderService } from '../shared/services/lab-billing-placeholder.service';
import { ServiceRequestBillingPlaceholderService } from '../shared/services/service-request-billing-placeholder.service';
import { inferServiceRequestType } from '../shared/service-request-type.util';
import { forkJoin, Observable, of } from 'rxjs';
import { AuthService } from '../shared/auth/auth.service';
export interface ServiceRequestRow extends TableRowModel {
  status?: string;
  patientRef?: string;
  codeDisplay?: string;
  authoredOn?: string;
}

@Injectable({ providedIn: 'root' })
export class ServiceRequestSearchService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<ServiceRequest, ServiceRequestRow>;
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false);
  private readonly serviceRequestsService = inject(ServiceRequestsService);
  private readonly labBillingPlaceholderService = inject(LabBillingPlaceholderService);
  private readonly serviceRequestBillingPlaceholderService = inject(ServiceRequestBillingPlaceholderService);
  private readonly auth = inject(AuthService);

  readonly dataSource = new MatTableDataSource<ServiceRequestRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly error$ = this.tableFiltering.error$;

  init(tab: string = 'all', patientId?: string | null): void {
    const defaultQuery = patientId ? `/ServiceRequest?subject=Patient/${patientId}` : `/ServiceRequest`;
    let CombinedQuery = defaultQuery;
    const allowedCategories = this.auth.getAllowedServiceRequestCategories();
// $#$    ///* alert(`Allowed categories for user: ${JSON.stringify(allowedCategories) */}`);
    const isRestricted = !this.auth.currentUserValue?.isSuperAdmin &&
      !['admin', 'doctor', 'nurse', 'consultant', 'receptionist', 'cashier', 'labtechnician'].includes(this.auth.currentUserValue?.role.toLowerCase() || '');
// $#$    /* alert(isRestricted); */
    if (tab != 'all' && tab !== 'others') {
      // Check if specifically requested tab is allowed
      const normalizedTab = tab.toLowerCase().trim();
      if (!allowedCategories.map((c: string) => c.toLowerCase()).includes(normalizedTab)) {
        // Return a query that returns nothing or enforce the restriction
        CombinedQuery += `&category=NOT_AUTHORIZED`;
      } else {
        CombinedQuery += `&category:text=${tab}`;
      }
    } else if (tab === 'others') {
      if (isRestricted) {
        // If restricted, "others" should only show categories they are allowed that are not core buckets.
        const otherAllowed = allowedCategories.filter((c: string) => !['laboratory', 'imaging', 'procedure', 'surgery', 'observation'].includes(c.toLowerCase()));
        if (otherAllowed.length > 0) {
          CombinedQuery += `&category=${otherAllowed.join(',')}`;
        } else {
          CombinedQuery += `&category=NOT_AUTHORIZED`;
        }
      } else {
        CombinedQuery += `&category:not=laboratory&category:not=imaging&category:not=procedure&category:not=surgery&category:not=observation`;
      }
    } else if (tab === 'all' && isRestricted) {
      // For restricted users, "All" is limited to their allowed categories
      if (allowedCategories.length > 0) {
        CombinedQuery += `&category=${allowedCategories.join(',')}`;
      } else {
        CombinedQuery += `&category=NOT_AUTHORIZED`;
      }
    }
    this.tableFiltering.init({
      resourceType: 'ServiceRequest',
      defaultQuery: CombinedQuery,
      dataSource: this.dataSource,
      pageSize: 25,
      baseUrl: this.backendUrl,
      transformEntry: (resource: ServiceRequest) => this.transform(resource),
      httpOptions: { context: this.silentHttpContext },
      disableCountQuery: true
    });

    // After page load, batch fetch side data (billing placeholders etc.)
    this.dataSource.connect().subscribe(rows => {
      if (rows && rows.length) {
        this.batchLoadSideData(rows as ServiceRequestRow[]);
      }
    });
  }

  destroy(): void {
    this.tableFiltering.destroy();
    this.dataSource.disconnect();
  }

  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.tableFiltering.refreshCurrentPage();
  }

  private transform(resource: ServiceRequest): ServiceRequestRow {
    return {
      id: resource.id ?? '',
      resourceType: 'ServiceRequest',
      rawResource: resource,
      status: resource.status,
      patientRef: resource.subject?.reference,
      codeDisplay: resource.code?.text || resource.code?.coding?.[0]?.display || '',
      authoredOn: resource.authoredOn || (resource as any).authoredDateTime
    } as ServiceRequestRow;
  }

  private batchLoadSideData(rows: ServiceRequestRow[]): void {
    const unloadedRows = rows.filter(r => (r as any).relatedChargeItems === undefined);
    if (!unloadedRows.length) return;

    const byPatient = new Map<string, ServiceRequestRow[]>();
    for (const row of unloadedRows) {
      const patientRef = (row.patientRef || '').toString().trim();
      const patientId = patientRef.split('/').pop() || '';
      if (!patientId) continue;
      const list = byPatient.get(patientId) || [];
      list.push(row);
      byPatient.set(patientId, list);
    }

    const jobs: Array<Observable<any>> = [];
    for (const [patientId, patientRows] of byPatient.entries()) {
      const labRows = patientRows.filter(r => inferServiceRequestType(r.rawResource as ServiceRequest) === 'laboratory');
      const imagingRows = patientRows.filter(r => inferServiceRequestType(r.rawResource as ServiceRequest) === 'imaging');
      const procedureRows = patientRows.filter(r => {
        const type = inferServiceRequestType(r.rawResource as ServiceRequest);
        return type === 'procedure' || type === 'surgery';
      });
      const otherRows = patientRows.filter(r => {
        const type = inferServiceRequestType(r.rawResource as ServiceRequest);
        return type !== 'laboratory' && type !== 'imaging' && type !== 'procedure' && type !== 'surgery' && type !== 'observation';
      });

      if (labRows.length > 0) {
        const job = this.labBillingPlaceholderService.batchResolveBillingAndPlaceholderForLab(labRows.map(r => r.rawResource as ServiceRequest)).pipe(
          map(res => {
            for (const r of labRows) {
              const srId = r.rawResource?.id;
              const billing = res && srId ? res[srId] : null;
              (r as any).relatedChargeItems = billing?.chargeItems || [];
              (r as any).relatedInvoices = billing?.invoices || [];
              (r as any).hasBalancedInvoice = !!billing?.hasBalancedInvoice;
            }
            return true;
          }),
          catchError(() => of(null))
        );
        jobs.push(job);
      }

      if (imagingRows.length > 0) {
        const job = this.labBillingPlaceholderService.batchResolveBillingAndPlaceholderForImagingStudy(imagingRows.map(r => r.rawResource as ServiceRequest)).pipe(
          map(res => {
            for (const r of imagingRows) {
              const srId = r.rawResource?.id;
              const billing = res && srId ? res[srId] : null;
              (r as any).relatedChargeItems = billing?.chargeItems || [];
              (r as any).relatedInvoices = billing?.invoices || [];
              (r as any).hasBalancedInvoice = !!billing?.hasBalancedInvoice;
            }
            return true;
          }),
          catchError(() => of(null))
        );
        jobs.push(job);
      }

      if (procedureRows.length > 0) {
        const job = this.labBillingPlaceholderService.batchResolveBillingAndPlaceholderForProcedure(procedureRows.map(r => r.rawResource as ServiceRequest)).pipe(
          map(res => {
            for (const r of procedureRows) {
              const srId = r.rawResource?.id;
              const billing = res && srId ? res[srId] : null;
              (r as any).relatedChargeItems = billing?.chargeItems || [];
              (r as any).relatedInvoices = billing?.invoices || [];
              (r as any).hasBalancedInvoice = !!billing?.hasBalancedInvoice;
            }
            return true;
          }),
          catchError(() => of(null))
        );
        jobs.push(job);
      }

      if (otherRows.length > 0) {
        const job = this.serviceRequestBillingPlaceholderService.batchResolvePaymentForServiceRequests(otherRows.map(r => r.rawResource as ServiceRequest)).pipe(
          map(res => {
            for (const r of otherRows) {
              const srId = r.rawResource?.id;
              const billing = res && srId ? res[srId] : null;
              (r as any).relatedChargeItems = billing?.chargeItems || [];
              (r as any).relatedInvoices = billing?.invoices || [];
              (r as any).hasBalancedInvoice = !!billing?.hasBalancedInvoice;
            }
            return true;
          }),
          catchError(() => of(null))
        );
        jobs.push(job);
      }
    }

    if (!jobs.length) return;

    forkJoin(jobs).subscribe(() => {
      try {
        const current = this.dataSource.data || [];
        this.dataSource.data = [...current];
      } catch (e) {
        // swallow
      }
    });
  }
}
